/**
 * Dérive la liste « outils MCP » documentaire depuis une spec OpenAPI 3.x du backend
 * (`GET /api/openapi.json`) : une entrée par opération, `operationId` = nom d’outil recommandé.
 */

import type { McpToolCategory, McpToolRow } from '@/lib/mcp-tools-catalog';

const HTTP_METHODS = new Set(['get', 'post', 'put', 'patch', 'delete', 'options', 'head']);

export interface OpenApiMcpOperation {
  operationId: string;
  method: string;
  path: string;
  summary: string;
  description: string;
  tag: string;
  source: string;
}

/** Retire les marqueurs **gras** Markdown pour un affichage UI simple. */
export function plainOpenApiDescription(text: string): string {
  return text.replace(/\*\*([^*]+)\*\*/g, '$1');
}

export function parseOpenApiToMcpOperations(spec: unknown): OpenApiMcpOperation[] {
  if (!spec || typeof spec !== 'object') return [];
  const paths = (spec as Record<string, unknown>).paths;
  if (!paths || typeof paths !== 'object') return [];

  const out: OpenApiMcpOperation[] = [];
  for (const [path, item] of Object.entries(paths as Record<string, unknown>)) {
    if (!item || typeof item !== 'object') continue;
    for (const [method, op] of Object.entries(item as Record<string, unknown>)) {
      const m = method.toLowerCase();
      if (!HTTP_METHODS.has(m)) continue;
      if (!op || typeof op !== 'object') continue;
      const o = op as Record<string, unknown>;
      const operationId = String(
        o.operationId ?? `${m}_${path.replace(/[/{}\s]/g, '_').replace(/_+/g, '_')}`,
      );
      const summary = String(o.summary ?? `${m.toUpperCase()} ${path}`);
      const description = typeof o.description === 'string' && o.description.length > 0 ? o.description : summary;
      const tags = Array.isArray(o.tags) ? o.tags : [];
      const tag = String((tags[0] as string | undefined) ?? 'api');
      const source = typeof o['x-source'] === 'string' ? o['x-source'] : '—';
      out.push({
        operationId,
        method: m.toUpperCase(),
        path,
        summary,
        description,
        tag,
        source,
      });
    }
  }
  out.sort(
    (a, b) =>
      a.tag.localeCompare(b.tag, 'fr') || a.path.localeCompare(b.path) || a.method.localeCompare(b.method),
  );
  return out;
}

function tagTitleFr(tag: string): string {
  if (tag.startsWith('v2/')) {
    const rest = tag.slice(3).replace(/-/g, ' ');
    return `API V2 — ${rest.charAt(0).toUpperCase()}${rest.slice(1)}`;
  }
  const spaced = tag.replace(/-/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function groupOpenApiOperationsByTag(ops: OpenApiMcpOperation[]): McpToolCategory[] {
  const map = new Map<string, OpenApiMcpOperation[]>();
  for (const op of ops) {
    if (!map.has(op.tag)) map.set(op.tag, []);
    map.get(op.tag)!.push(op);
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b, 'fr'))
    .map(([tag, list]) => {
      const tools: McpToolRow[] = list.map((op) => ({
        name: op.operationId,
        description: op.description,
        http: `${op.method} ${op.path}`,
      }));
      return {
        id: tag.replace(/[^a-zA-Z0-9_-]/g, '_'),
        title: tagTitleFr(tag),
        tools,
        openApiTag: tag,
      };
    });
}

export function countOpenApiOperations(spec: unknown): number {
  return parseOpenApiToMcpOperations(spec).length;
}
